// YatraNest Nepal — Payment Architecture
// Providers: eSewa, Khalti, FonePay, Pay Later, Cash
// NOTE: This is the integration architecture layer.
// Actual payment provider credentials must be configured server-side.
// Never expose secret keys in frontend code.
// ⚠️ SANDBOX/TEST MODE — Label clearly when credentials are unavailable.

export type PaymentProvider = 'esewa' | 'khalti' | 'fonepay' | 'cash' | 'pay_later';
export type PaymentStatus = 'unpaid' | 'pending' | 'paid' | 'failed' | 'refunded' | 'cancelled';

export const PAYMENT_ENV = process.env.NEXT_PUBLIC_PAYMENT_ENV || 'sandbox';
export const IS_SANDBOX = PAYMENT_ENV !== 'production';

export interface PaymentInitParams {
  bookingId: string;
  amount: number; // in NPR paisa (amount * 100)
  provider: PaymentProvider;
  returnUrl: string;
  failureUrl: string;
  productName: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
}

export interface PaymentVerifyParams {
  provider: PaymentProvider;
  transactionId: string;
  bookingId: string;
  amount: number;
}

export interface PaymentRecord {
  id: string;
  bookingId: string;
  provider: PaymentProvider;
  status: PaymentStatus;
  amount: number;
  transactionRef?: string;
  pidx?: string; // Khalti payment index
  refId?: string; // eSewa reference ID
  traceId?: string; // FonePay trace ID
  initiatedAt: string;
  paidAt?: string;
  failedAt?: string;
  refundedAt?: string;
  refundRef?: string;
  errorMessage?: string;
  isSandbox?: boolean;
}

// ─── eSewa ────────────────────────────────────────────────────────────────────
// eSewa sandbox: https://uat.esewa.com.np
// eSewa production: https://esewa.com.np
// Docs: https://developer.esewa.com.np
// ⚠️ IMPORTANT: Never expose ESEWA_SECRET_KEY in frontend code.
// Verification must happen server-side only via /api/payments/verify

export const ESEWA_CONFIG = {
  merchantCode: process.env.NEXT_PUBLIC_ESEWA_MERCHANT_CODE || 'EPAYTEST',
  sandboxUrl: 'https://uat.esewa.com.np/epay/main',
  productionUrl: 'https://esewa.com.np/epay/main',
  verifyUrl: 'https://uat.esewa.com.np/epay/transrec', // server-side only
  isProduction: PAYMENT_ENV === 'production',
  isSandbox: IS_SANDBOX,
};

export function buildEsewaPaymentUrl(params: {
  amount: number;
  taxAmount: number;
  totalAmount: number;
  transactionUuid: string;
  productCode: string;
  successUrl: string;
  failureUrl: string;
}): { url: string; formData: Record<string, string> } {
  const baseUrl = ESEWA_CONFIG.isProduction ? ESEWA_CONFIG.productionUrl : ESEWA_CONFIG.sandboxUrl;
  const formData: Record<string, string> = {
    amt: String(params.amount),
    txAmt: String(params.taxAmount),
    psc: '0',
    pdc: '0',
    tAmt: String(params.totalAmount),
    pid: params.transactionUuid,
    scd: ESEWA_CONFIG.merchantCode,
    su: params.successUrl,
    fu: params.failureUrl,
  };
  return { url: baseUrl, formData };
}

// ─── Khalti ───────────────────────────────────────────────────────────────────
// Khalti sandbox: https://a.khalti.com
// Khalti production: https://khalti.com
// Docs: https://docs.khalti.com
// ⚠️ IMPORTANT: KHALTI_SECRET_KEY must only be used server-side.
// Frontend uses public key only for initiating payment.

export const KHALTI_CONFIG = {
  publicKey: process.env.NEXT_PUBLIC_KHALTI_PUBLIC_KEY || 'test_public_key_dc74e0fd57cb46cd93832aee0a390234',
  sandboxUrl: 'https://a.khalti.com/api/v2',
  productionUrl: 'https://khalti.com/api/v2',
  isProduction: PAYMENT_ENV === 'production',
  isSandbox: IS_SANDBOX,
};

export interface KhaltiInitPayload {
  return_url: string;
  website_url: string;
  amount: number; // in paisa
  purchase_order_id: string;
  purchase_order_name: string;
  customer_info: {
    name: string;
    email: string;
    phone?: string;
  };
}

// ─── FonePay ──────────────────────────────────────────────────────────────────
// FonePay: https://fonepay.com
// Docs: https://developer.fonepay.com
// ⚠️ IMPORTANT: FONEPAY_SECRET_KEY must only be used server-side.

export const FONEPAY_CONFIG = {
  merchantCode: process.env.NEXT_PUBLIC_FONEPAY_MERCHANT_CODE || 'FONEPAY_TEST',
  sandboxUrl: 'https://dev-clientapi.fonepay.com/api/merchantRequest',
  productionUrl: 'https://clientapi.fonepay.com/api/merchantRequest',
  isProduction: PAYMENT_ENV === 'production',
  isSandbox: IS_SANDBOX,
};

// ─── Payment Status Helpers ───────────────────────────────────────────────────

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, { en: string; ne: string; color: string }> = {
  unpaid: { en: 'Unpaid', ne: 'भुक्तानी नभएको', color: 'text-snow/50 bg-white/5' },
  pending: { en: 'Pending', ne: 'प्रतीक्षारत', color: 'text-gold bg-gold/10' },
  paid: { en: 'Paid', ne: 'भुक्तानी भयो', color: 'text-green-400 bg-green-400/10' },
  failed: { en: 'Failed', ne: 'असफल', color: 'text-red-400 bg-red-400/10' },
  refunded: { en: 'Refunded', ne: 'फिर्ता भयो', color: 'text-sky bg-sky/10' },
  cancelled: { en: 'Cancelled', ne: 'रद्द भयो', color: 'text-snow/40 bg-white/5' },
};

export const PAYMENT_PROVIDER_INFO: Record<PaymentProvider, { label: string; icon: string; color: string; description: string; sandboxNote?: string }> = {
  esewa: {
    label: 'eSewa',
    icon: '💚',
    color: 'border-green-500/40 bg-green-500/5',
    description: 'Pay via eSewa digital wallet',
    sandboxNote: 'Test mode — use eSewa sandbox credentials',
  },
  khalti: {
    label: 'Khalti',
    icon: '💜',
    color: 'border-purple-500/40 bg-purple-500/5',
    description: 'Pay via Khalti digital wallet',
    sandboxNote: 'Test mode — use Khalti test credentials',
  },
  fonepay: {
    label: 'FonePay',
    icon: '📱',
    color: 'border-blue-500/40 bg-blue-500/5',
    description: 'Pay via FonePay QR / mobile banking',
    sandboxNote: 'Test mode — FonePay sandbox',
  },
  pay_later: {
    label: 'Pay Later',
    icon: '🕐',
    color: 'border-amber-500/40 bg-amber-500/5',
    description: 'Reserve now, pay within 24 hours',
  },
  cash: {
    label: 'Cash / Manual',
    icon: '💵',
    color: 'border-gold/40 bg-gold/5',
    description: 'Pay in cash — confirmed by provider',
  },
};

export function generateTransactionRef(provider: PaymentProvider, bookingId: string): string {
  const prefix = provider.toUpperCase().slice(0, 3);
  const timestamp = Date.now().toString(36).toUpperCase();
  const bookingShort = bookingId.slice(0, 8).toUpperCase();
  return `YN-${prefix}-${bookingShort}-${timestamp}`;
}

export function getPaymentStatusBadge(status: PaymentStatus, lang: 'en' | 'ne' = 'en') {
  const info = PAYMENT_STATUS_LABELS[status] || PAYMENT_STATUS_LABELS.unpaid;
  return {
    label: lang === 'ne' ? info.ne : info.en,
    className: `text-xs px-2 py-0.5 rounded-full font-medium ${info.color}`,
  };
}

// ─── Sandbox Warning ─────────────────────────────────────────────────────────
// Display this prominently in the UI when IS_SANDBOX is true
export const SANDBOX_WARNING = {
  en: '⚠️ TEST MODE — Payments are not processed. For demo/testing only.',
  ne: '⚠️ परीक्षण मोड — भुक्तानी प्रक्रिया हुँदैन। केवल डेमो/परीक्षणको लागि।',
};

// ─── Security Notes ───────────────────────────────────────────────────────────
// 1. Payment verification MUST happen server-side via /api/payments/verify
// 2. Never mark payment_status = 'paid' from frontend code
// 3. Never expose ESEWA_SECRET_KEY, KHALTI_SECRET_KEY, FONEPAY_SECRET_KEY in frontend
// 4. All payment callbacks must be validated with HMAC/signature on the server
// 5. Transaction references are for tracking only — not for verification
